import { Fragment, RefObject } from "react"
import classNames from "classnames"
import { observer } from "mobx-react"
import { Header, Footer } from "@features/common"
import { SmartBanner } from "@features/smart-banner"
import { SeoH1 } from "@features/seo-h1"

import type { ReactNode, ReactElement } from "react"

export type CommonTemplateProps = {
  children: ReactNode | ReactNode[] | null
  footer?: ReactElement<typeof Footer> | null
  seoH1?: string
  header?: ReactElement<typeof Header> | null
  headerRef?: RefObject<HTMLDivElement>
  alternativeHeader?: boolean
  mainClassName?: string
  smartBanner?: ReactElement<typeof SmartBanner> | null
}

export const CommonTemplate = observer(function CommonTemplate(
  props: CommonTemplateProps,
) {
  const {
    children,
    smartBanner = null,
    header = null,
    seoH1 = "",
    footer = null,
    headerRef,
    mainClassName = "",
    alternativeHeader = false,
  } = props

  const headerClasses = classNames(
    "absolute z-2 right-0 left-0 flex flex-col bg-transparent h-auto",
    smartBanner && "top-20 tablet:top-13",
  )

  const mainClasses = classNames(
    mainClassName,
    // if page has no header and no smartbanner
    !header && !smartBanner && "top-0",
    // if page only has header
    header &&
      !smartBanner &&
      (alternativeHeader
        ? "top-13 tablet-m:top-18"
        : "top-16 tablet:top-20 desktop:top-22"),
    // if page has header & smartBanner
    header &&
      smartBanner &&
      (alternativeHeader
        ? "top-[132px] tablet-m:top-[156px] tablet:top-[124px]"
        : "top-[144px] tablet:top-[132px] dekstop:top-[140px]"),
  )

  return (
    <Fragment>
      {smartBanner ? (
        <div className="absolute w-full top-0">{smartBanner}</div>
      ) : null}
      {header ? (
        <div ref={headerRef} className={headerClasses}>
          {header}
        </div>
      ) : null}
      <main className={mainClasses}>
        <Fragment>{children}</Fragment>
        {Boolean(seoH1) && seoH1.length > 0 && <SeoH1 title={seoH1} />}
        {footer ? <Fragment>{footer}</Fragment> : null}
      </main>
    </Fragment>
  )
})
